import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { RippleModule } from 'primeng/ripple';
import { exhaustMap, filter, finalize, map, switchMap, timer } from 'rxjs';
import { IconKey } from '../../../../../../whitelabel/icon-key.enum';
import { OverlayPanelComponent } from '../../../../../osp-ui/components/overlay/overlay-panel/overlay-panel.component';
import { AcknowledgeAutomatedTransferNotificationCommand } from '../../../../../pages/transfers/contract/acknowledge-automated-transfer-notificaton-command.contract';
import { AutomatedTransferNotificationTypeToBallSeverityPipe } from '../../../../../shared/pipes/automated-transfer-notification-to-ball-severity.pipe';
import { IconPipe } from '../../../../../shared/pipes/icon.pipe';
import { AuthService } from '../../../../services/auth.service';
import { AcknowledgeAllNotificationsButtonComponent } from '../shared/acknowledge-all-button/acknowledge-all-button.component';
import { AcknowledgeNotificationButtonComponent } from '../shared/acknowledge-notification-button/acknowledge-notification-button.component';
import { NoNotificationFoundComponent } from '../shared/no-notification-found/no-notification-found.component';
import { NotificationOverlayTogglerComponent } from '../shared/notification-overlay-toggler/notification-overlay-toggler.component';
import { NotificationSeverityBallComponent } from '../shared/notification-severity-ball/notification-severity-ball.component';
import { OverlayNotificationComponent } from '../shared/overlay-notification/overlay-notification.component';
import { ATNotificationToMessageService } from './services/at-notification-to-message.service';
import { AutomatedTransfersNotificationsDatasourceService } from './services/automated-transfers-notifications-datasource.service';

@Component({
  selector: 'osp-automated-transfers-notifications-center-overlay',
  standalone: true,
  imports: [
    OverlayPanelComponent,
    CommonModule,
    RippleModule,
    TranslateModule,
    IconPipe,
    NotificationSeverityBallComponent,
    AcknowledgeNotificationButtonComponent,
    OverlayNotificationComponent,
    NotificationOverlayTogglerComponent,
    AutomatedTransferNotificationTypeToBallSeverityPipe,
    AcknowledgeAllNotificationsButtonComponent,
    NoNotificationFoundComponent,
  ],
  templateUrl: './automated-transfers-notifications-center-overlay.component.html',
  styleUrls: ['./automated-transfers-notifications-center-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutomatedTransfersNotificationsCenterOverlayComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly notificationDatasourceService = inject(AutomatedTransfersNotificationsDatasourceService);
  private readonly authService = inject(AuthService);
  private readonly aTNotificationToMessageService = inject(ATNotificationToMessageService);

  @ViewChild('op') overlayPanel!: OverlayPanelComponent;

  readonly IconKey = IconKey;

  private readonly FETCH_NOTIFICATIONS_INTERVAL_MS = 30000; // 30 seconds

  readonly notifications$ = this.notificationDatasourceService.currentActive$.pipe(
    map((notifications) => this.aTNotificationToMessageService.convert(notifications)),
  );

  readonly notificationsTotalAmount$ = this.notificationDatasourceService.currentActiveTotalCount$;
  readonly $isNotificationsError = this.notificationDatasourceService.$isNotificationsError;

  $isLoadingAcknowledge = signal(false);
  $isLoadingAcknowledgeAll = signal(false);

  ngOnInit(): void {
    this.getNotificationsAndCounts();
  }

  onItemClick(redirectCb: (() => void) | undefined | null): void {
    this.overlayPanel.hide();

    if (redirectCb) {
      redirectCb();
    }
  }

  acknowledge(id?: string): void {
    if (!id) return;

    const command = { id } as AcknowledgeAutomatedTransferNotificationCommand;
    this.$isLoadingAcknowledge.set(true);

    this.notificationDatasourceService
      .acknowledgeAutomatedTransferNotification(command)
      .pipe(
        switchMap(() => this.notificationDatasourceService.refreshCurrentActive()),
        finalize(() => this.$isLoadingAcknowledge.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  acknowledgeAll(): void {
    this.$isLoadingAcknowledgeAll.set(true);
    this.notificationDatasourceService
      .acknowledgeAllAutomatedTransferNotifications()
      .pipe(
        switchMap(() => this.notificationDatasourceService.refreshCurrentActive()),
        finalize(() => this.$isLoadingAcknowledgeAll.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private getNotificationsAndCounts(): void {
    if (this.authService.isTokenExpired()) {
      this.authService.logout();
      return;
    }
    this.notificationDatasourceService.currentActiveCountUpdates$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    timer(0, this.FETCH_NOTIFICATIONS_INTERVAL_MS)
      .pipe(
        filter(() => !this.authService.isTokenExpired()),
        exhaustMap(() => this.notificationDatasourceService.refreshCurrentActive()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
